import {defineDimension, createBooleanMap} from '@webflow-platform/abilities';
import {Map} from 'immutable';
import {getPage} from '@packages/shared/utils/PageTreeUtils';
import {isBranchPage} from '@packages/shared/utils/PageUtils';
import {
  BRANCH_MODES,
  type BranchModeType,
} from '@packages/systems/designer/page';
import PageRecord, {
  type PageState,
} from '@packages/systems/core/records/PageRecord';

export type BranchesConfig = Partial<Record<BranchModeType, boolean>>;
export type BranchesStores = {
  DesignerStore?: {currentPageId?: string | undefined};
  PageStore?: PageState;
};

export const getCurrentBranchMode = (state: BranchesStores) => {
  // Use `in` before reading: partial DesignerStores proxies from
  // `createPartialDesignerStoreForTest` throw on missing keys even with `?.`.
  const currentPageId =
    'DesignerStore' in state ? state.DesignerStore?.currentPageId : undefined;
  const currentPage =
    'PageStore' in state && state.PageStore
      ? getPage(currentPageId, state.PageStore)
      : null;
  return currentPage && isBranchPage(currentPage)
    ? BRANCH_MODES.BRANCH
    : BRANCH_MODES.MAIN;
};

// Mirrors the legacy Designer abilities snapshot harness's `createPageStore`
// so the testMatrix cases below match its branchId case universe exactly.
const createPageStore = (branchId: string | null) =>
  Map({
    staticPages: Map({
      'some-page': PageRecord({id: 'some-page', type: 'Page', branchId}),
    }),
    dynamicPages: Map(),
    componentPages: Map(),
  });

export const branchesDimension = defineDimension<
  BranchesConfig,
  BranchesStores
>()({
  key: 'branches',
  isValid(state, config) {
    const currentBranchMode = getCurrentBranchMode(state);

    return Boolean(config[currentBranchMode]);
  },
  fragments: {
    inBranches: (...modes: BranchModeType[]) => createBooleanMap(modes, true),
  },
  testMatrix(addAxis) {
    addAxis((addCase) => {
      addCase({
        label: `branchId === 'some-branch-id'`,
        state: {
          DesignerStore: {currentPageId: 'some-page'},
          PageStore: createPageStore('some-branch-id'),
        },
      });
      addCase({
        label: 'branchId === null',
        state: {
          DesignerStore: {currentPageId: undefined},
          PageStore: createPageStore(null),
        },
      });
    });
  },
});

export const {inBranches} = branchesDimension.fragments;
